/**
 * 宠物与专属领养模块：建立伙伴（上传自己的宠物 / 原子独占领养）、公开主页读取。
 * live：照片以 multipart 上传，服务端校验格式与大小、剥离元数据、私有存储。
 * fixture：演示候选池；不上传照片（明确提示），不创建真实归属。
 */
import type { WorldBodyCareView, CareRequestView, AdoptionCandidate, AdoptResult, BodyProfileView, BodyStatementResult, PetMotionSet, CharacterRegenerateResult, CharacterState, PetAlbumPage, PetPhotoDetail, PetCoreFactsView, PetDNAView, PetPrivateSummary, PetPublicProfile, PhotoRequestView, PostPage, PublicPetView, PublicResident, PublicResidentTimeline, PublicWorld, TimelineItem } from "@/shared/contracts";
import { ApiError } from "@/shared/api/errors";
import { defineModule } from "@/shared/modules/types";
import { calibrate } from "@/shared/time/clock";
import { fixtureAdopt, fixtureCandidates } from "@/fixtures/adoption";
import { fixtureArrivalCharacter, fixtureArrivalRedraw } from "@/fixtures/arrival";
import { fixturePublicProfile } from "@/fixtures/social";
import { delay } from "@/fixtures/world";
import { AddCompanionPage, AdoptPage, OnboardingPage } from "./pages";
import { PendingEntryPage } from "./PendingEntryPage";
import { PublicPetPage, PublicWorldPage } from "./PublicWorldPage";
import { Navigate } from "react-router";

function fixtureResidents(): PublicResident[] {
  return fixtureCandidates().filter((candidate) => candidate.availability === "available").map((candidate) => ({
    pet_id: candidate.pet_id ?? `fx-resident-${candidate.candidate_id}`,
    candidate_id: candidate.candidate_id,
    name: candidate.name,
    species: candidate.species,
    personality: candidate.personality,
    dream: candidate.dream,
    origin: candidate.origin,
    source_note: candidate.source_note,
    residence: "演示居民驿站",
    city: "演示星球",
    living_since: "2026-09-22T00:00:00Z",
    presence: "at_home",
    doing: "在演示驿站休息",
    place_name: null,
    recent_posts: [],
  }));
}

/** 明确的演示公开投影；不读取成员资料，也不创建真实归属。 */
async function fixturePublicPet(petId: string): Promise<PublicPetView> {
  const resident = fixtureResidents().find((item) => item.pet_id === petId);
  if (!resident) throw new ApiError({ kind: "http", status: 404, code: "NOT_FOUND", message: "没有找到这位居民。" });
  return delay<PublicPetView>({
    profile: { pet_id: resident.pet_id, display_name: resident.name, species: resident.species, avatar_url: null, bio: resident.personality, origin_label: resident.source_note, visibility: "public", follower_count: 0, post_count: 0, viewer_follows: false, is_own: false, data_origin: "fixture" },
    resident, adoptable: true, posts: [],
  });
}

async function fixturePublicWorld(): Promise<PublicWorld> {
  return delay<PublicWorld>({
    server_time: "2026-09-22T00:00:00Z",
    entries: [
      { route: "browse", label: "先逛逛", needs_login: false, note: "演示公开世界" },
      { route: "own_pet", label: "带我的宠物来", needs_login: true, note: "演示模式不创建账号" },
      { route: "adopt", label: "认识新伙伴", needs_login: true, note: "演示居民" },
    ],
    residents: fixtureResidents(), recent_posts: [], living_residents: fixtureResidents().length, cache_seconds: 30, data_origin: "fixture",
  });
}

export default defineModule({
  id: "pets",
  routes: [{ path: "pets/new", element: <AddCompanionPage /> }],
  bareRoutes: [
    // 旧拍照页已删（用户 10-09「不要拍照页」，PHOTO-ASK-FLOW-01）：/photos 只是一条内联重定向，让旧链接、旧通知落到相册，不是页面；相册自己做家庭守卫。
    { path: "photos", element: <Navigate to="/album" replace state={null} /> },
    { path: "onboarding", element: <OnboardingPage /> },
    { path: "onboarding/choice", element: <PendingEntryPage /> },
    { path: "adopt", element: <AdoptPage /> },
    { path: "world", element: <PublicWorldPage /> },
    { path: "world/residents/:petId", element: <PublicPetPage /> },
  ],
  services: {
    pets: {
      fixture: () => ({
        worldBodyCare: async () => { throw ApiError.capability("care.world_body", "演示模式不能提交真实护理提议。"); },
        proposeWorldBodyCare: async () => { throw ApiError.capability("care.world_body", "演示模式不能提交真实护理提议。"); },
        cancelWorldBodyCare: async () => { throw ApiError.capability("care.world_body", "演示模式不能取消真实护理提议。"); },
        motion: async () => null,
        bodyProfile: async () => { throw ApiError.capability("pets.body", "演示模式没有真实身体档案。"); },
      submitBody: async () => { throw ApiError.capability("pets.body_profile", "演示模式没有真实身体档案。"); },
      confirmBody: async () => { throw ApiError.capability("pets.body_profile", "演示模式没有真实身体档案。"); },
      rejectBody: async () => { throw ApiError.capability("pets.body_profile", "演示模式没有真实身体档案。"); },
      keepBodyConflict: async () => { throw ApiError.capability("pets.body_profile", "演示模式没有真实身体档案。"); },
      correctBodyConflict: async () => { throw ApiError.capability("pets.body_profile", "演示模式没有真实身体档案。"); },
        photoRequests: async () => { throw ApiError.capability("pets.photo-requests", "演示模式没有真实照片列表。"); },
        album: async () => delay<PetAlbumPage>({ items: [], next_cursor: null, total_visible: 0 }),
        // 演示相册是空的（不编 TA 带回来的照片），没有哪一张能点开；直接打开详情地址时如实说演示里没有
        albumPhoto: async () => { throw ApiError.capability("pets.album", "演示模式没有相册照片。"); },
        // 从相册移除（WIRE-01 A7）：演示相册是空的，没有哪一张能移除
        hideAlbumPhoto: async () => { throw ApiError.capability("pets.album", "演示模式没有相册照片。"); },
        // P2 聊天回放（6796 起草）：演示里没有宠物之间的聊天，不编；如实说没有
        petChats: async () => { throw ApiError.capability("pets.pet-chats", "演示里没有 TA 和朋友的聊天。"); },
        petChat: async () => { throw ApiError.capability("pets.pet-chats", "演示里没有 TA 和朋友的聊天。"); },
        publicWorld: fixturePublicWorld,
        publicWorldGuest: fixturePublicWorld,
        publicResidents: async () => delay(fixtureResidents()),
        publicPet: fixturePublicPet,
        publicPetGuest: fixturePublicPet,
        publicPetPosts: async () => delay<PostPage>({ items: [], next_cursor: null }),
        adoptionCandidates: () => delay(fixtureCandidates()),
        adopt: async (id, key) => delay(fixtureAdopt(id, key)),
        createOwn: async () => {
          throw ApiError.capability("pets.create_own", "演示模式不上传照片，也不创建真实宠物。");
        },
        addPhoto: async () => {
          throw ApiError.capability("pets.add_photo", "演示模式不上传照片。");
        },
        replacePhoto: async () => {
          throw ApiError.capability("pets.replace_photo", "演示模式不上传照片。");
        },
        publicProfile: async (petId) => {
          const profile = fixturePublicProfile(petId);
          if (!profile) throw new ApiError({ kind: "http", status: 404, code: "NOT_FOUND", message: "没有找到这只宠物的公开主页。" });
          return delay(profile);
        },
        // 演示模式没有专属世界形象：不拿内部样例猫冒充任何宠物的角色——小窝、形象页在演示模式本来就不读这两个（只在正式数据下读）。
        // 只有到家等待页在演示模式读，演一段「没画好 → 再画一次 → 到家」（CREATE-PET-REGEN-01，I 10-09；不放图，见 fixtures/arrival）。
        character: async (petId) => delay(fixtureArrivalCharacter(petId), 120),
        regenerateCharacter: async (petId, _body, key) => delay(fixtureArrivalRedraw(petId, key), 120),
        regenerateIdPhoto: async () => { throw ApiError.capability("character.id_photo", "演示模式不能画证件照。"); /* 和按钮「画证件照」同名；不写「生成」（UI-GEN-WORDING-01） */ },
        // 演示世界没有主人交代过的 DNA：不编性格与习惯。
        dna: async () => { throw ApiError.capability("pets.dna", "演示模式没有 TA 的档案。"); },
        saveDna: async () => { throw ApiError.capability("pets.dna", "演示模式不能保存档案。"); },
        // 演示世界没有这只宠物的性别与生日（PET-FACT-01）：不编。
        facts: async () => { throw ApiError.capability("pets.facts", "演示模式没有 TA 的性别和生日。"); },
        saveFacts: async () => { throw ApiError.capability("pets.facts", "演示模式不能保存性别和生日。"); },
        // 允许 TA 谈恋爱（WIRE-01 A4）：演示世界没有这只宠物的这项设置，不编；设置页在演示里本来就没有当前宠物，这一项不显示。
        socialPrefs: async () => { throw ApiError.capability("pet.social_prefs", "演示模式没有这一项设置。"); },
        saveSocialPrefs: async () => { throw ApiError.capability("pet.social_prefs", "演示模式不能改这一项设置。"); },
        // 演示世界没有一条连贯的生活记录：各份样例的时间互不相干（收藏里“第一次坐飞机”在一小时前，卡包里的登机牌三周前就用过），拼起来等于替 TA 编经历。
        timeline: async () => { throw ApiError.capability("life.timeline", "演示模式没有生活片段。"); },
      }),
      live: ({ api }) => ({
        worldBodyCare: (petId, signal) => api.request<WorldBodyCareView>(`/pets/${encodeURIComponent(petId)}/world-body`, { method: "GET", signal }),
        proposeWorldBodyCare: (petId, body, key) => api.request<CareRequestView>(`/pets/${encodeURIComponent(petId)}/world-body/requests`, { method: "POST", body, idempotencyKey: key }),
        cancelWorldBodyCare: (petId, requestId, body, key) => api.request<CareRequestView>(`/pets/${encodeURIComponent(petId)}/world-body/requests/${encodeURIComponent(requestId)}/cancel`, { method: "POST", body, idempotencyKey: key }),
        motion: (petId, signal) => api.request<PetMotionSet | null>(`/pets/${encodeURIComponent(petId)}/motion`, { method: "GET", query: { purpose: "home" }, signal }),
        bodyProfile: (petId, signal) => api.request<BodyProfileView>(`/pets/${encodeURIComponent(petId)}/body`, { method: "GET", signal }),
      submitBody: (petId, body, key) => api.request<BodyStatementResult>(`/pets/${encodeURIComponent(petId)}/body/statements`, { method: "POST", body, idempotencyKey: key }),
      confirmBody: (petId, sid, body, key) => api.request<BodyProfileView>(`/pets/${encodeURIComponent(petId)}/body/statements/${encodeURIComponent(sid)}/confirm`, { method: "POST", body, idempotencyKey: key }),
      rejectBody: (petId, sid, key) => api.request<BodyProfileView>(`/pets/${encodeURIComponent(petId)}/body/statements/${encodeURIComponent(sid)}/reject`, { method: "POST", body: {}, idempotencyKey: key }),
      keepBodyConflict: (petId, cid, body, key) => api.request<BodyProfileView>(`/pets/${encodeURIComponent(petId)}/body/conflicts/${encodeURIComponent(cid)}/keep`, { method: "POST", body, idempotencyKey: key }),
      correctBodyConflict: (petId, cid, body, key) => api.request<BodyProfileView>(`/pets/${encodeURIComponent(petId)}/body/conflicts/${encodeURIComponent(cid)}/correct`, { method: "POST", body, idempotencyKey: key }),
        photoRequests: (petId, signal) => api.request<PhotoRequestView[]>(`/pets/${encodeURIComponent(petId)}/photo-requests`, { signal }),
        album: (petId, params, signal) => {
          const query = new URLSearchParams({ limit: String(params.limit) });
          if (params.cursor) query.set("cursor", params.cursor);
          if (params.category) query.set("category", params.category);
          return api.request<PetAlbumPage>(`/pets/${encodeURIComponent(petId)}/album?${query.toString()}`, { signal });
        },
        albumPhoto: (petId, photoId, signal) =>
          api.request<PetPhotoDetail>(`/pets/${encodeURIComponent(petId)}/album/${encodeURIComponent(photoId)}`, { signal }),
        // 从相册移除（WIRE-01 A7）：没有请求体；幂等键走请求头，CSRF 由客户端对写操作自动回填
        hideAlbumPhoto: (petId, photoId, key) =>
          api.request<import("@/shared/contracts").AlbumHideResult>(`/pets/${encodeURIComponent(petId)}/album/${encodeURIComponent(photoId)}/hide`, { method: "POST", idempotencyKey: key }),
        // P2 聊天回放（6796 起草）：两条纯读路由；游标原样带回
        petChats: (petId, params, signal) => {
          const query = new URLSearchParams();
          if (params.limit) query.set("limit", String(params.limit));
          if (params.cursor) query.set("cursor", params.cursor);
          if (params.friendId) query.set("friend_id", params.friendId);
          const suffix = query.toString() ? `?${query.toString()}` : "";
          return api.request<import("@/shared/contracts").PetChatPage>(`/pets/${encodeURIComponent(petId)}/pet-chats${suffix}`, { signal });
        },
        petChat: (petId, threadId, signal) =>
          api.request<import("@/shared/contracts").PetChatReplay>(`/pets/${encodeURIComponent(petId)}/pet-chats/${encodeURIComponent(threadId)}`, { signal }),
        publicWorld: async () => {
          const world = await api.request<PublicWorld>("/public/world");
          calibrate(world.server_time);
          return world;
        },
        publicWorldGuest: async () => {
          if (!api.publicGuestWorldGet) throw ApiError.capability("public.guest_world_read");
          const world = await api.publicGuestWorldGet<PublicWorld>();
          calibrate(world.server_time);
          return world;
        },
        publicResidents: () => api.request<PublicResident[]>("/public/residents"),
        publicPet: (petId) => api.request<PublicPetView>(`/public/pets/${encodeURIComponent(petId)}`),
        publicPetGuest: async (petId) => {
          if (!api.publicGuestGet) throw ApiError.capability("public.guest_read");
          return api.publicGuestGet<PublicPetView>(`/public/pets/${encodeURIComponent(petId)}`);
        },
        // TA 的公开时间线（居民主页「TA 的星球生活」）：照详情分两路；不带分页参数（后端默认一页 30 条）
        publicResidentTimeline: (petId, signal) => api.request<PublicResidentTimeline>(`/public/residents/${encodeURIComponent(petId)}/timeline`, { signal }),
        publicResidentTimelineGuest: async (petId, signal) => {
          if (!api.publicGuestResidentTimelineGet) throw ApiError.capability("public.guest_read");
          return api.publicGuestResidentTimelineGet<PublicResidentTimeline>(petId, signal ? { signal } : {});
        },
        publicPetPosts: (petId, cursor) => api.request<PostPage>(`/public/pets/${encodeURIComponent(petId)}/posts`, { query: { cursor } }),
        adoptionCandidates: () => api.request<AdoptionCandidate[]>("/adoption/candidates"),
        // 给了 householdId 才带 household_id（迎进已有的家）；null 时请求体仍是 {candidate_id}，与原来逐字相同（新建家庭）。
        adopt: (id, key, householdId) =>
          api.request<AdoptResult>("/adoption/adopt", { method: "POST", body: householdId ? { candidate_id: id, household_id: householdId } : { candidate_id: id }, idempotencyKey: key }),
        createOwn: (input, key) => {
          const form = new FormData();
          form.set("name", input.name);
          form.set("species", input.species);
          form.set("sex", input.sex);
          if (input.photo) form.set("photo", input.photo);
          if (input.householdId) form.set("household_id", input.householdId);
          return api.request<PetPrivateSummary>("/pets", { method: "POST", body: form, idempotencyKey: key, timeoutMs: 30_000 });
        },
        // 入住时没带照片、之后补一张（add_pet_photo）：multipart 字段 photo；CSRF 由客户端对写操作自动回填，幂等键走请求头。只补不换。
        // 照片一变（补、换）后端自动排一次证件照（2026-09-26），形象不跟着画；前端只重读形象状态，不另发生成请求。
        addPhoto: (petId, photo, key) => {
          const form = new FormData();
          form.set("photo", photo);
          return api.request<PetPrivateSummary>(`/pets/${encodeURIComponent(petId)}/photo`, { method: "PUT", body: form, idempotencyKey: key, timeoutMs: 30_000 });
        },
        // 换掉现在那张（同一个 add_pet_photo，多带 replace_version）：版本原样用服务端 409 details 里的 version，不自己推算。
        replacePhoto: (petId, photo, replaceVersion, key) => {
          const form = new FormData();
          form.set("photo", photo);
          form.set("replace_version", replaceVersion);
          return api.request<PetPrivateSummary>(`/pets/${encodeURIComponent(petId)}/photo`, { method: "PUT", body: form, idempotencyKey: key, timeoutMs: 30_000 });
        },
        publicProfile: (petId) => api.request<PetPublicProfile>(`/pets/${encodeURIComponent(petId)}/profile`),
        character: (petId, signal) => api.request<CharacterState>(`/pets/${encodeURIComponent(petId)}/character`, { signal }),
        regenerateCharacter: (petId, body, key) => api.request<CharacterRegenerateResult>(`/pets/${encodeURIComponent(petId)}/character/regenerate`, { method: "POST", body, idempotencyKey: key }),
        regenerateIdPhoto: (petId, key) => api.request<CharacterRegenerateResult>(`/pets/${encodeURIComponent(petId)}/id-photo/regenerate`, { method: "POST", idempotencyKey: key }),
        dna: (petId, signal) => api.request<PetDNAView>(`/pets/${encodeURIComponent(petId)}/dna`, { signal }),
        saveDna: (petId, body, expectedVersion) =>
          api.request<PetDNAView>(`/pets/${encodeURIComponent(petId)}/dna`, { method: "PUT", body, query: { expected_version: expectedVersion } }),
        // TA 的性别与生日（PET-FACT-01）：整体保存，expected_version 在请求体里（首存 0）；CSRF 由客户端对写操作自动回填，幂等键走请求头。
        facts: (petId, signal) => api.request<PetCoreFactsView>(`/pets/${encodeURIComponent(petId)}/facts`, { signal }),
        saveFacts: (petId, body, key) => api.request<PetCoreFactsView>(`/pets/${encodeURIComponent(petId)}/facts`, { method: "PUT", body, idempotencyKey: key }),
        // 允许 TA 谈恋爱（WIRE-01 A4）：整体保存，expected_version 在请求体里；幂等键走请求头
        socialPrefs: (petId, signal) => api.request<import("@/shared/contracts").PetSocialPrefsView>(`/pets/${encodeURIComponent(petId)}/social-prefs`, { signal }),
        saveSocialPrefs: (petId, body, key) =>
          api.request<import("@/shared/contracts").PetSocialPrefsView>(`/pets/${encodeURIComponent(petId)}/social-prefs`, { method: "PUT", body, idempotencyKey: key }),
        timeline: (petId, signal) => api.request<TimelineItem[]>("/timeline", { query: { pet_id: petId }, signal }),
      }),
    },
  },
});
